import type { QueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import { getReport, startReport } from "@/api/visits";
import { VisitReportPage } from "@/customers/VisitReportPage";
import { pageTitle } from "@/i18n/title";
import { query } from "@/queries";

/**
 * Opening the report is starting it. A scheduled visit with no report gets
 * one before the page paints, so there is no separate start screen.
 */
async function ensureReport(queryClient: QueryClient, visitId: string) {
  const options = query.visits.report(visitId);
  if (await queryClient.ensureQueryData(options)) return;
  const visit = await queryClient.ensureQueryData(query.visits.visit(visitId));
  if (visit.status !== "scheduled") return;
  try {
    queryClient.setQueryData(options.queryKey, await startReport(visitId));
  } catch (error) {
    // A second open can lose the race and get "not scheduled" after the first
    // start already created the report.
    const started = await getReport(visitId);
    if (!started) throw error;
    queryClient.setQueryData(options.queryKey, started);
  }
  await queryClient.invalidateQueries({ queryKey: query.visits.base });
}

function ReportLoadError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : "";
  return (
    <p className="p-6 text-destructive text-sm" role="alert">
      {message}
    </p>
  );
}

export const Route = createFileRoute(
  "/_authed/customers/$customerId/visits/$visitId/"
)({
  head: () => pageTitle("Visit report"),
  // Opening the report POSTs startReport. Intent preload must not create one.
  preload: false,
  loader: ({ context, params }) =>
    Promise.all([
      ensureReport(context.queryClient, params.visitId),
      // The page reads these with suspense. Seed them here — ensureReport
      // skips the visit once a report exists — or the route suspends again
      // and pending flashes a second time. Attachments stay off this path.
      context.queryClient.ensureQueryData(query.visits.visit(params.visitId)),
      context.queryClient.ensureQueryData(
        query.customers.accountDetail(params.customerId)
      ),
    ]),
  errorComponent: ReportLoadError,
  component: VisitReportPage,
});
